import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CardGiftcard as ReferralIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { getReferrals, updateReferral } from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';
import ModernTextField from '../../components/common/ModernTextField';

const ReferralsManagement = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { showToast } = useToast();

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const { data } = await getReferrals({ page, limit: 20 });
      setReferrals(data.referrals);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
      showToast('Failed to fetch referrals', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [page]);

  const handleEditReferral = (referral) => {
    setEditForm({
      _id: referral._id,
      referralCode: referral.referralCode,
      usedCount: referral.usedCount,
    });
    setEditDialog(true);
  };

  const handleSaveReferral = async () => {
    try {
      const { _id, ...updateData } = editForm;
      await updateReferral(_id, updateData);
      showToast('Referral updated successfully', 'success');
      setEditDialog(null);
      fetchReferrals();
    } catch (error) {
      showToast('Failed to update referral', 'error');
    }
  };

  const columns = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      renderCell: (params) => params.row.userId ? (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.userId.firstName} {params.row.userId.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.userId.email}
          </Typography>
        </Box>
      ) : 'N/A',
    },
    {
      field: 'referralCode',
      headerName: 'Referral Code',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.referralCode}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, fontFamily: 'monospace' }}
        />
      ),
    },
    {
      field: 'usedCount',
      headerName: 'Used Count',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.usedCount}
          color={params.row.usedCount > 0 ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'isStudent',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isStudent ? 'Student' : 'Other'}
          size="small"
          color={params.row.isStudent ? 'info' : 'default'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => handleEditReferral(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'warning.main', width: 48, height: 48, mr: 2 }}>
            <ReferralIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Referrals Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage all referral codes and tracking
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Data Grid */}
      <ModernDataGrid
        rows={referrals}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pagination
        page={page}
        onPageChange={(newPage) => setPage(newPage + 1)}
        pageSize={20}
        rowCount={totalPages * 20}
        paginationMode="server"
      />

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit Referral</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label="Referral Code"
                  value={editForm.referralCode}
                  onChange={(e) => setEditForm({ ...editForm, referralCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label="Used Count"
                  type="number"
                  value={editForm.usedCount}
                  onChange={(e) => setEditForm({ ...editForm, usedCount: parseInt(e.target.value) || 0 })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" onClick={handleSaveReferral}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ReferralsManagement;
